// City-specific landing page powering /standort/:slug routes.
// Pulls structured data from /data/cities.js and renders a fast, SEO-rich page
// with LocalBusiness JSON-LD scoped to that city. Falls back to a 404 if the
// city slug is unknown.
import { useParams, Link, Navigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { CITIES, getCityBySlug } from "../data/cities";
import SEOHead from "../components/SEOHead";
import { ArrowRight, MapPin, Clock3, Users, Sparkles, CheckCircle2 } from "lucide-react";
import { BUSINESS_PROFILE } from "../data/seo";

export default function CityPage() {
  const { slug } = useParams();
  const { t } = useLang();
  const city = getCityBySlug(slug);

  if (!city) return <Navigate to="/" replace />;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Talkani ${city.name}`,
    "image": `${BUSINESS_PROFILE.url}${city.image}`,
    "telephone": BUSINESS_PROFILE.telephone,
    "email": BUSINESS_PROFILE.email,
    "url": `${BUSINESS_PROFILE.url}/standort/${city.slug}`,
    "priceRange": "€€",
    "areaServed": { "@type": "City", "name": city.name },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city.name,
      "addressCountry": "DE",
      "postalCode": city.plz_range.split("–")[0],
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": city.geo.lat,
      "longitude": city.geo.lng,
    },
    "description": city.seo_tagline,
  };

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <SEOHead
        title={`Talkani ${city.name} — Logistik, Reinigung & Service | Express 90 Min.`}
        description={city.seo_tagline}
        url={`${BUSINESS_PROFILE.url}/standort/${city.slug}`}
        type="website"
        jsonLd={[localBusinessSchema]}
      />

      {/* Hero */}
      <section className="relative bg-[#090A0B] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url(${city.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#090A0B] via-[#090A0B]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="flex items-center gap-2 text-[#FF5A00] mb-4">
            <MapPin size={16} />
            <span className="text-xs font-bold tracking-[0.3em] uppercase">Talkani Standort</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 leading-[0.95]">
            {city.headline}
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed">
            {city.subhead}
          </p>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/book"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid={`city-hero-book-${city.slug}`}
            >
              Jetzt buchen <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${BUSINESS_PROFILE.telephone.replace(/\s/g, "")}`}
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
            >
              {BUSINESS_PROFILE.telephone}
            </a>
          </div>

          {/* Stat strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 max-w-3xl">
            <StatBlock icon={Clock3} label="Antwortzeit" value={`${city.response_time_min} Min.`} />
            <StatBlock icon={Users} label="Einwohner" value={city.population} />
            <StatBlock icon={MapPin} label="PLZ-Bereich" value={city.plz_range} />
            <StatBlock icon={Sparkles} label="Bezirke" value={`${city.coverage_districts.length}+`} />
          </div>
        </div>
      </section>

      {/* Specialties — what we do best in this city */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">
            Unsere Leistungen in {city.name}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-12 max-w-3xl">
            Vor Ort. Schnell. Persönlich.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {city.specialties.map((s) => (
              <div
                key={s.key}
                className="border border-neutral-200 p-7 hover:border-[#FF5A00] transition-all bg-white group"
                data-testid={`city-specialty-${s.key}`}
              >
                <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#FF5A00] mb-3">
                  {city.name}
                </div>
                <h3 className="text-xl font-black tracking-tight mb-3">{s.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed mb-6">{s.text}</p>
                <Link
                  to={`/book?service=${s.key}`}
                  className="inline-flex items-center gap-1.5 text-[#FF5A00] font-bold text-sm tracking-wide hover:gap-2 transition-all"
                  data-testid={`city-book-${s.key}`}
                >
                  Jetzt buchen <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Districts coverage */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA] border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">
            Wir bedienen in {city.name}
          </p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-10">
            {city.coverage_districts.length}+ Stadtteile
          </h2>
          <div className="flex flex-wrap gap-2.5">
            {city.coverage_districts.map((d) => (
              <span
                key={d}
                className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 px-4 py-2 text-sm font-medium hover:border-[#FF5A00] hover:text-[#FF5A00] transition"
                data-testid={`city-district-${d}`}
              >
                <CheckCircle2 size={13} className="text-[#FF5A00]" />
                {d}
              </span>
            ))}
          </div>
          <p className="mt-10 text-sm text-neutral-500 max-w-2xl leading-relaxed">
            Ihr Stadtteil ist nicht gelistet? Wir bedienen auch das Umland — rufen Sie an oder buchen Sie online,
            wir prüfen die Machbarkeit kostenlos.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#090A0B] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-6">
            Bereit für Talkani {city.name}?
          </h2>
          <p className="text-neutral-300 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Online buchen in unter 60 Sekunden. Festpreis-Garantie. Persönliche Betreuung.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to={`/book`}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-8 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid={`city-cta-book-${city.slug}`}
            >
              Jetzt buchen <ArrowRight size={16} />
            </Link>
            <Link
              to="/"
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-8 py-4 transition rounded-sm"
            >
              Alle Standorte
            </Link>
          </div>
        </div>
      </section>

      {/* All cities cross-link footer (SEO + UX) */}
      <section className="py-14 bg-white border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-neutral-500 mb-5">
            Talkani an weiteren Standorten
          </p>
          <div className="flex flex-wrap gap-2.5">
            {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
              <Link
                key={c.slug}
                to={`/standort/${c.slug}`}
                className="border border-neutral-200 px-4 py-2 text-sm font-medium hover:border-[#FF5A00] hover:text-[#FF5A00] transition"
                data-testid={`city-link-${c.slug}`}
              >
                Talkani {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const StatBlock = ({ icon: Icon, label, value }) => (
  <div className="border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
    <Icon size={16} className="text-[#FF5A00] mb-2" />
    <div className="text-[10px] font-bold tracking-widest uppercase text-neutral-400 mb-1">{label}</div>
    <div className="text-lg font-black text-white">{value}</div>
  </div>
);
